import { LegalScopeNotice } from "@/components/LegalScopeNotice";
import { FREE_DAILY_USES, PREMIUM_DAILY_USES } from "@/lib/plans";
import { Link } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BackToHomeLink } from "@/components/BackToHomeLink";

export default function TermsOfService() {
  usePageMeta({
    title: "Terms of Service",
    description: "The terms governing your use of FreeDailyPro.",
    path: "/terms",
  });

  return (
    <div className="container max-w-3xl mx-auto py-12 px-4">
      <BackToHomeLink />
      <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Last updated: October 14, 2026
      </p>

      <LegalScopeNotice />

      <div className="prose prose-sm max-w-none space-y-8 text-sm leading-relaxed text-foreground/90">
        <section>
          <p>
            These Terms of Service ("Terms") govern your use of freedailypro.com
            (the "Service"), operated by DreamUSA LLC ("we," "us," or "our"). By
            using the Service, you agree to these Terms. If you do not agree,
            please do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">1. The Service</h2>
          <p>
            FreeDailyPro provides a collection of browser-based tools (PDF,
            image, text, and other utilities). Most tools process your files
            entirely within your own browser; we do not upload or store your
            files on our servers.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">
            2. Trial Uses and Paid Plans
          </h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              All tools, including file-based ones, can be used with no
              account required, up to {FREE_DAILY_USES} trial uses per day on Professional Tools and {PREMIUM_DAILY_USES} on Premium Applications (tracked
              anonymously per browser for file-based tools, with no personal
              information involved). One trial use is one visit to a tool:
              everything you do in that tool until you leave its page counts
              once. Non-file tools (calculators, generators,
              converters) are unlimited for everyone. An account is only
              needed to purchase a paid plan for unlimited file-based tool
              use.
            </li>
            <li>
              <strong>Day Pass</strong> ($1/day): a one-time payment granting
              unlimited use of file-based tools for 24 hours.
            </li>
            <li>
              <strong>Monthly Pro</strong> ($4.99/month): an auto-renewing
              monthly subscription granting unlimited use of file-based tools
              and an ad-free experience for as long as it's active.
            </li>
            <li>
              Monthly Pro renews automatically each billing period until
              cancelled. You can cancel anytime from your Profile page, or by contacting us; your access
              continues until the end of the current paid period.
            </li>
            <li>
              All payments are processed by Stripe. Refunds are considered on a
              case-by-case basis — contact us at support@freedailypro.com.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">3. Acceptable Use</h2>
          <p className="mb-2">You agree not to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Use the Service for any unlawful purpose or to process content you
              don't have the right to use
            </li>
            <li>
              Attempt to bypass usage limits, security measures, or
              reverse-engineer the Service
            </li>
            <li>
              Use automated means (bots, scrapers) to access the Service beyond
              normal use
            </li>
            <li>
              Interfere with or disrupt the Service or servers/networks
              connected to it
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">
            4. Intellectual Property
          </h2>
          <p>
            The Service, including its design, branding, and underlying code, is
            owned by DreamUSA LLC. You retain all rights to the files and
            content you process using the Service.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">
            5. Disclaimer of Warranties
          </h2>
          <p>
            The Service is provided "as is" and "as available," without
            warranties of any kind, express or implied. We do not guarantee the
            Service will be uninterrupted, error-free, or that tool output will
            meet your specific requirements. This applies in particular to:
            tools relying on heuristic extraction (e.g., PDF table/text
            extraction), where results can vary depending on the source file;
            financial, tax, health, and retirement calculators, which provide
            estimates based on simplified or general-purpose formulas and are
            not professional financial, tax, or medical advice; and
            general-purpose legal document templates, which are not a substitute
            for advice from a qualified attorney.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">
            6. Limitation of Liability
          </h2>
          <p>
            To the fullest extent permitted by law, DreamUSA LLC shall not be
            liable for any indirect, incidental, special, or consequential
            damages arising from your use of the Service.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">7. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service if you
            violate these Terms. You may stop using the Service and request
            account deletion at any time.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">8. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of North Carolina,
            USA, without regard to conflict-of-law principles.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">
            9. Changes to These Terms
          </h2>
          <p>
            We may update these Terms from time to time. Continued use of the
            Service after changes take effect constitutes acceptance of the
            updated Terms.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">Guest messaging (Event Suite Premium)</h2>
          <p>
            Invitations, RSVP links and text reminders are sent by you, as the
            organiser, to people you invite. You are responsible for having
            those guests' permission and for following the messaging and
            privacy rules where you and your guests are. Text messages go
            through your own Twilio account and number, so Twilio's terms, fees
            and carrier registration are between you and Twilio. We built this
            feature to the best of our knowledge at the time of development
            (a guest chooses text on the RSVP page, STOP is honoured, quiet
            hours are enforced), but it is not legal advice. If a local rule
            conflicts with how a feature works, tell us at the address below
            and, where needed, check with your own advisor.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2">10. Contact Us</h2>
          <p>
            Questions about these Terms? Email us at{" "}
            <a
              href="mailto:support@freedailypro.com"
              className="text-primary underline"
            >
              support@freedailypro.com
            </a>{" "}
            or visit our{" "}
            <Link href="/contact" className="text-primary underline">
              Contact page
            </Link>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
